import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';
import SubscriptionList from '@/components/SubscriptionList';

type StatusFilter = 'all' | 'ACTIVE' | 'TRIALING' | 'PAST_DUE' | 'CANCELED' | 'INCOMPLETE';

export default function AdminSubscriptionsPage() {
  const { t } = useTranslation();
  const apiClient = useApiClient();
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const pageSize = 50;

  const subscriptionsQuery = useQuery({
    queryKey: ['admin:subscriptions', page],
    queryFn: () => apiClient.getAdminSubscriptions(page * pageSize, pageSize),
  });

  const subscriptions = subscriptionsQuery.data?.data || [];
  const total = subscriptionsQuery.data?.total || 0;

  const filteredSubscriptions = statusFilter === 'all'
    ? subscriptions
    : subscriptions.filter((s: any) => s.status === statusFilter);

  const activeCount = subscriptions.filter((s: any) => s.status === 'ACTIVE').length;
  const trialCount = subscriptions.filter((s: any) => s.status === 'TRIALING').length;
  const pastDueCount = subscriptions.filter((s: any) => s.status === 'PAST_DUE').length;
  const canceledCount = subscriptions.filter((s: any) => s.status === 'CANCELED').length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900">💳 {t('admin.subscriptionTitle')}</h1>
        <p className="text-neutral-600 mt-2">{t('admin.subscriptionSubtitle')}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <button
          onClick={() => setStatusFilter('all')}
          className={`p-4 rounded-lg border transition-all ${statusFilter === 'all' ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500/20' : 'border-neutral-200 bg-white hover:border-blue-300'}`}
        >
          <p className="text-sm text-neutral-600">{t('admin.total')}</p>
          <p className="text-2xl font-bold text-neutral-900">{total}</p>
        </button>
        <button
          onClick={() => setStatusFilter('ACTIVE')}
          className={`p-4 rounded-lg border transition-all ${statusFilter === 'ACTIVE' ? 'border-green-500 bg-green-50 ring-2 ring-green-500/20' : 'border-neutral-200 bg-white hover:border-green-300'}`}
        >
          <p className="text-sm text-green-600">{t('admin.active')}</p>
          <p className="text-2xl font-bold text-green-700">{activeCount}</p>
        </button>
        <button
          onClick={() => setStatusFilter('TRIALING')}
          className={`p-4 rounded-lg border transition-all ${statusFilter === 'TRIALING' ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500/20' : 'border-neutral-200 bg-white hover:border-blue-300'}`}
        >
          <p className="text-sm text-blue-600">{t('admin.trial')}</p>
          <p className="text-2xl font-bold text-blue-700">{trialCount}</p>
        </button>
        <button
          onClick={() => setStatusFilter('PAST_DUE')}
          className={`p-4 rounded-lg border transition-all ${statusFilter === 'PAST_DUE' ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-500/20' : 'border-neutral-200 bg-white hover:border-orange-300'}`}
        >
          <p className="text-sm text-orange-600">{t('admin.pastDue')}</p>
          <p className="text-2xl font-bold text-orange-700">{pastDueCount}</p>
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setStatusFilter('CANCELED')}
          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${statusFilter === 'CANCELED' ? 'bg-red-100 text-red-700 ring-2 ring-red-500/20' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}
        >
          {t('admin.canceled')} ({canceledCount})
        </button>
        <button
          onClick={() => setStatusFilter('INCOMPLETE')}
          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${statusFilter === 'INCOMPLETE' ? 'bg-gray-100 text-gray-700 ring-2 ring-gray-500/20' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}
        >
          {t('admin.incomplete')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <h2 className="font-semibold text-neutral-900">
            {statusFilter === 'all' ? t('admin.allSubscriptions') : `${t('admin.subscriptions')} ${statusFilter.toLowerCase()}`}
          </h2>
          <span className="text-sm text-neutral-500">{t('admin.results', { count: filteredSubscriptions.length })}</span>
        </div>
        <SubscriptionList
          subscriptions={filteredSubscriptions}
          isLoading={subscriptionsQuery.isLoading}
        />
      </div>

      {total > pageSize && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-neutral-600">
            {t('admin.showingPage', {
              from: page * pageSize + 1,
              to: Math.min((page + 1) * pageSize, total),
              total,
            })}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="w-full sm:w-auto px-4 py-2 rounded-lg border border-neutral-200 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('admin.previous')}
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={(page + 1) * pageSize >= total}
              className="w-full sm:w-auto px-4 py-2 rounded-lg border border-neutral-200 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('admin.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
